/**
 * NavIcon — fixed-box icon used by every nav link so icons with uneven
 * internal whitespace align on a common baseline. Renders the custom SVG icon
 * set (AppIcon) when an `icon` name is provided, else falls back to the PNG.
 */
import { cn } from '../../../lib/utils';
import AppIcon, { type AppIconName } from '../../ui/AppIcon';

interface NavIconProps {
  src: string;
  /** Custom SVG icon — takes precedence over `src` and is theme-tintable. */
  icon?: AppIconName;
  active?: boolean;
  /** Invert for dark (sukhasan) theme so dark PNGs stay visible. */
  invert?: boolean;
  iconClass?: string;
  /** Box + image size, e.g. 'w-4 h-4' (desktop) or 'w-[18px] h-[18px]' (mobile). */
  sizeClass?: string;
  /** Extra classes for the <img>, e.g. an opacity override. */
  imgClassName?: string;
}

// Box size in px so the SVG matches the PNG icon footprint per surface.
const SIZE_PX: Record<string, number> = {
  'w-4 h-4': 16,
  'w-[18px] h-[18px]': 18,
  'w-5 h-5': 20,
};

export default function NavIcon({
  src,
  icon,
  active = false,
  invert = false,
  iconClass,
  sizeClass = 'w-4 h-4',
  imgClassName,
}: NavIconProps) {
  return (
    <span className={cn('flex-shrink-0 inline-flex items-center justify-center', sizeClass)}>
      {icon ? (
        // currentColor inherits the link's text color (active = kesri), so the
        // icon tints with the theme/active state automatically.
        <AppIcon
          name={icon}
          size={SIZE_PX[sizeClass] ?? 16}
          color="currentColor"
          className={cn(active ? 'opacity-100' : 'opacity-70', iconClass)}
        />
      ) : (
        <img
          src={src}
          alt=""
          className={cn(
            'object-contain',
            sizeClass,
            active ? 'opacity-100' : 'opacity-70',
            invert && 'invert brightness-200',
            iconClass,
            imgClassName,
          )}
        />
      )}
    </span>
  );
}
